import React from 'react';
import InputBox from './input-box';
import { OptionType } from './select';
import useLocale from '../util/i18n';

type AutoCompleteProps = {
    value?: string;
    defaultValue?: string;
    required?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    forceFill?: boolean;
    label?: string;
    className?: string;
    wrapperClass?: string;
    load?: () => Promise<Array<OptionType>>;
    options?: Array<OptionType> | Record<string, Array<OptionType>>;
    children?: JSX.Element;
    comment?: string;
    multiple?: boolean;
    autoCapitalize?: string;
    strict?: boolean;
} & ({
    name: string;
    onChange?: (e: { target: { name: string, value: string } }) => void;
} | {
    name?: undefined;
    onChange?: (e: { target: { value: string } }) => void;
})

export default function AutoComplete(props: AutoCompleteProps) {
    const { 0: value, 1: setValue } = React.useState<string | undefined>(typeof props.value === 'undefined' || props.value === null ? props.defaultValue : props.value);
    const { 0: options, 1: setOptions } = React.useState<Array<OptionType>>([]);
    const { 0: __ } = useLocale();
    const filtered = React.useMemo<Array<OptionType>>(() => {
        let result = [];
        if (value) {
            for (let option of options) {
                if (option.value.includes(value) || option.label.includes(value)) {
                    result.push(option);
                }
            }
        }
        return result;
    }, [value, options]);
    const { 0: focus, 1: setFocus } = React.useState(false);
    const ref = React.useRef<HTMLInputElement>(null);
    const onChange = React.useCallback((value: string | { target: { value: string } }, e?: React.MouseEvent) => {
        e?.stopPropagation();
        e?.preventDefault();
        if (typeof value === 'object') {
            value = value.target.value;
        }
        //@ts-ignore
        props.onChange ? props.onChange({ target: { name: props.name, value } }) : setValue(value);
    }, [props.onChange, props.name, setValue]);
    const onBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        if (props.forceFill && options.length) {
            if (filtered.length) {
                onChange(filtered[0].value);
                ref.current?.setCustomValidity('');
            } else {
                ref.current?.setCustomValidity(__('Not Found'));
                ref.current?.reportValidity();
            }
        }
        setFocus(false);
    }, [filtered, options, props.forceFill, onChange, setFocus]);
    const onTransitionEnd = React.useCallback((e: React.TransitionEvent<HTMLElement>) => {
        e.currentTarget.style.pointerEvents = parseFloat(e.currentTarget.style.opacity) > 0 ? 'all' : 'none';
    }, []);
    const debounce = React.useRef(false);
    React.useEffect(() => {
        if (props.load) {
            if (debounce.current) {
                return;
            }
            debounce.current = true;
            props.load().then(options => {
                setOptions(options);
                // if (!props.children) {
                //     onChange(options[0].value);
                // }
                debounce.current = false;
            });
        }
    }, [props.load, onChange, setOptions]);
    React.useEffect(() => {
        if (props.options) {
            let options: Array<OptionType> = [];
            if (Array.isArray(props.options)) {
                options = props.options;
            } else {
                for (let k in props.options) {
                    options = options.concat(props.options[k]);
                }
            }
            setOptions(options);
            if (!props.children) {
                onChange(options[0].value);
            }
        }
    }, [props.options, onChange, setOptions]);
    React.useEffect(() => {
        setValue(props.value);
    }, [props.value, setValue]);
    const tmp: any = {};
    for (let i of ['defaultValue', 'required', 'name', 'readOnly', 'disabled']) {
        if (typeof props[(i as 'name')] !== 'undefined') {
            tmp[i] = props[(i as 'name')];
        }
    }
    return (
        <InputBox label={props.label} required={props.required || false} className={props.wrapperClass} comment={props.comment}>
            <>
                <input
                    className={props.className ? "form-control active " + props.className : "form-control active"}
                    {...tmp}
                    value={value}
                    onChange={onChange}
                    onFocus={props.readOnly || props.disabled ? undefined : setFocus.bind(null, true)}
                    onBlur={props.readOnly || props.disabled ? undefined : onBlur}
                    autoComplete="off"
                    ref={ref}
                />
                <ul className="auto-complete" style={focus ? { opacity: 1 } : { opacity: 0 }} onTransitionEnd={onTransitionEnd}>
                    {filtered.map((option, index) => (
                        <li key={tmp.name + '-' + option.value + '-' + index}
                            onClick={onChange.bind(null, option.value)}
                        >{option.label}</li>
                    ))}
                </ul>
            </>
        </InputBox>
    );
}